import { motion } from "framer-motion";
import { ClipboardList, ShieldCheck, Monitor, Package, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const ICONS = { clipboard: ClipboardList, shield: ShieldCheck, monitor: Monitor, package: Package };

const AuditSection = () => {
  const { t } = useLanguage();
  const e = t.eroeffnung;
  return (
    <section id="eroeffnung" className="scroll-mt-24 bg-dark py-20 lg:py-32">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-16">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow>{e.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 max-w-xl font-display text-4xl font-medium tracking-tight sm:text-5xl">{e.title}</h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-inkmuted">{e.sub}</p>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2">
              {e.points.map((p, i) => {
                const Icon = ICONS[p.icon] || ClipboardList;
                return (
                  <Reveal key={p.title} delay={0.1 + i * 0.06}>
                    <div className="flex items-start gap-4">
                      <span className="inline-flex rounded-full border border-gold/25 bg-gold/10 p-2.5 text-gold">
                        <Icon size={16} />
                      </span>
                      <div>
                        <h3 className="font-display text-lg font-medium text-bone">{p.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-inkmuted">{p.copy}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={0.3}>
              <div className="mt-12">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-inkmuted">{e.target}</p>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {e.targets.map((tg) => (
                    <span key={tg} className="rounded-full border border-bone/15 bg-carddark px-4 py-1.5 text-xs text-bone/75">{tg}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="lg:col-span-5">
            <motion.div
              data-testid="eroeffnung-package-card"
              whileHover={{ y: -4 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-[3px] border border-gold/30 bg-gradient-to-b from-goldsoft/30 to-carddark lg:sticky lg:top-28"
            >
              <div className="relative h-56 overflow-hidden lg:h-64">
                <img src="/eroeffnung-beratung.jpg" alt={e.pkgTitle} loading="lazy" style={{ objectPosition: "50% 30%" }} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-carddark via-dark/20 to-transparent" aria-hidden="true" />
                <span className="absolute left-5 top-5 inline-block rounded-full bg-gold px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink">{e.pkgBadge}</span>
              </div>
              <div className="p-8 lg:p-10 lg:pt-6">
              <h3 className="font-display text-3xl font-medium tracking-tight text-bone">{e.pkgTitle}</h3>
              <p className="mt-4 text-sm leading-relaxed text-bone/70">{e.pkgCopy}</p>
              <div className="mt-8 border-t border-bone/10 pt-6">
                <p className="font-display text-4xl font-bold text-gold">{e.pkgPrice}</p>
                <p className="mt-1 text-xs text-inkmuted">{e.pkgUnit}</p>
              </div>
              <button
                type="button"
                data-testid="eroeffnung-package-cta"
                onClick={() => scrollToId("kontakt")}
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-[3px] bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone"
              >
                {e.pkgCta}
                <ArrowUpRight size={15} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </button>
              </div>
            </motion.div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default AuditSection;
